// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

export { ICD10_VALUESET } from '../utils/icd10';
export const RXNORM_VALUESET = 'http://www.nlm.nih.gov/research/umls/rxnorm/vs';

export const MEDICATION_UNIT_OPTIONS = [
  { value: 'mg', label: 'mg' },
  { value: 'mcg', label: 'mcg' },
  { value: 'g', label: 'g' },
  { value: 'mL', label: 'mL' },
  { value: 'tablet', label: 'tablet' },
  { value: 'capsule', label: 'capsule' },
  { value: 'drop', label: 'drop' },
  { value: 'spray', label: 'spray' },
  { value: 'unit', label: 'unit' },
];

export const MEDICATION_FREQUENCY_OPTIONS = [
  { value: 'once-daily', label: 'Once daily' },
  { value: 'twice-daily', label: 'Twice daily' },
  { value: 'three-times-daily', label: 'Three times daily' },
  { value: 'four-times-daily', label: 'Four times daily' },
  { value: 'every-morning', label: 'Every morning' },
  { value: 'every-evening', label: 'Every evening' },
  { value: 'every-bedtime', label: 'At bedtime' },
  { value: 'as-needed', label: 'As needed' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

export const POLARIS_COMPOSITION_TYPE_SYSTEM = 'http://polarissleep.com/composition-types';
export const POLARIS_INTAKE_COMPOSITION_CODE = 'intake';
export const STEDI_ELIGIBILITY_IDENTIFIER_SYSTEM = 'http://stedi.com/eligibility-response';

/**
 * Catalog of clinical assessments exposed in the Medical Info tab. `name` maps to
 * the Questionnaire.name attribute in `apps/provider-app/data/questionnaires.json`.
 */
export const ASSESSMENT_CATALOG = [
  {
    key: 'medical-history',
    title: 'Medical History',
    description: 'Sleep history, medical conditions, and eligibility information from the intake questionnaire.',
    questionnaireName: 'eligibility-check-page-1',
    itemCount: 0,
    itemLabel: 'Questions',
  },
  {
    key: 'stop-bang',
    title: 'STOP-BANG Questionnaire',
    description:
      'An 8-question screening tool that estimates the risk of obstructive sleep apnea based on common clinical risk factors.',
    questionnaireName: 'stop-bang',
    itemCount: 8,
    itemLabel: 'Questions',
  },
  {
    key: 'epworth-sleepiness-scale',
    title: 'Epworth Sleepiness Scale',
    description:
      'An 8-item self-report scale that measures how likely a person is to doze off in common daytime situations.',
    questionnaireName: 'epworth-sleepiness-scale',
    itemCount: 8,
    itemLabel: 'Items',
  },
  {
    key: 'idiopathic-hypersomnia-severity-scale',
    title: 'Idiopathic Hypersomnia Severity Scale',
    description:
      'A 14-item patient-reported scale designed to measure the severity of idiopathic hypersomnia symptoms such as sleep inertia, daytime sleepiness, and unrefreshing sleep.',
    questionnaireName: 'idiopathic-hypersomnia-severity-scale',
    itemCount: 8,
    itemLabel: 'Questions',
  },
  {
    key: 'restless-leg-syndrome-questionnaire',
    title: 'Restless Leg Syndrome Questionnaire',
    description:
      'A symptom-based questionnaire used to assess features of restless legs syndrome, especially the urge to move the legs and symptom worsening at rest or at night.',
    questionnaireName: 'restless-leg-syndrome-questionnaire',
    itemCount: 8,
    itemLabel: 'Questions',
  },
  {
    key: 'swiss-narcolepsy-scale',
    title: 'Swiss Narcolepsy Scale',
    description:
      'A brief 5-item questionnaire used to screen for narcolepsy by assessing daytime sleepiness and cataplexy-like symptoms.',
    questionnaireName: 'swiss-narcolepsy-scale',
    itemCount: 5,
    itemLabel: 'Items',
  },
] as const;

export type AssessmentCatalogEntry = (typeof ASSESSMENT_CATALOG)[number];
